import {Injectable} from "@angular/core";
import {FormFieldGroupConfig} from "../../../model/api/form-field-group-config";
import {FormFieldConfig} from "../../../model/api/form-field-config";
import {ClearingCrudComponent} from "../../../crystal-clear/support/clearing-crud/clearing-crud.component";
import {Clearing} from "../../../model/clearing/clearing";
import {resolveFieldTypes, FieldKind} from "../../../utils/object-reflection";
import {buildFormField} from "../../../utils/form-helper";
import {FieldRecord} from "../../../model/api/field-record";
import {SupportClearingDataLoadingService} from "./support-clearing-data-loading.service";
import {Account} from "../../../model/clearing/account";
import TYPE = FormFieldConfig.TYPE;
import {TableColumnUtils} from "../../../utils/table-column-utils";
import {TableColumnConfig} from "../../../model/api/table-column-config";

@Injectable({
  providedIn: 'root'
})
export class SupportClearingFormConfigService {

  public static readonly fieldTypes = resolveFieldTypes<Clearing>({

    // ------------------------------------------------------------------
    // ENUMS
    // ------------------------------------------------------------------
    state: 'enum',
    openClose: 'enum',

    // ------------------------------------------------------------------
    // NUMBERS
    // ------------------------------------------------------------------
    volume: 'number',
    overrideAvgPrice: 'number',
    brokerageFee: 'number',
    overrideBrokerageFee: 'number',
    brokerageRateAmount: 'number',

    // ------------------------------------------------------------------
    // BOOLEANS
    // ------------------------------------------------------------------
    giveUp: 'boolean',
    exported: 'boolean',

    // ------------------------------------------------------------------
    // DATETIME (Luxon)
    // ------------------------------------------------------------------
    clearingDate: 'date',
    creationDate: 'datetime',
    modificationDate: 'datetime',
    sendingDate: 'datetime',
    confirmationDate: 'datetime',

    // ------------------------------------------------------------------
    // OBJECTS
    // ------------------------------------------------------------------
    account: 'object',
    trade: 'object',
    order: 'object'

  });


  public loadConfig(component: ClearingCrudComponent): FormFieldGroupConfig[] {

    const record = component.newRecord;
    if (!record) {
      return [];
    }

    const autoFields: FormFieldConfig[] =
      (Object.keys(record) as Array<keyof Clearing>)
        .filter(field => !this.isExcluded(field))
        .map(field =>
          buildFormField<Clearing>(field, SupportClearingFormConfigService.fieldTypes[field])
        );

    autoFields.forEach(field => field.options = SupportClearingFormConfigService.buildOptions(field, component.dataLoadingService));

    const accountFields: FormFieldConfig[] =
      (Object.keys(record.account) as Array<keyof Account>)
        .map(field => buildFormField<Account>(field, SupportClearingFormConfigService.fieldTypes[field]));
    const accountFieldTableConfig: TableColumnConfig[] = (Object.keys(component.newRecord.account ?? {}) as Array<keyof Account>)
      .map(field => {
        return TableColumnUtils.buildColumn<Account>(
          field,
          SupportClearingFormConfigService.fieldTypes[field],
        );
      });

    return [
      {
        group: 'account',
        fields: [
          {
            field: 'account',
            column: 12,
            type: TYPE.SINGLERICHFIELD,
            richFieldConfig: {
              fieldId: 'name',
              fieldName: 'name',
              tableConfigs: accountFieldTableConfig,
              fields: [
                {
                  group: '',
                  fields: accountFields
                }]
            }
          }
        ]
      }
      , {
        group: 'fields',
        fields: autoFields
      }
    ];
  }

  private isExcluded(field: keyof Clearing): boolean {
    return [
      'clearings',
      'clearingInfo',
      'account',
      'trade',
      'order'
    ].includes(field as string);
  }

  // ---------------------------------------------------------------------
  // Clearing-specific options
  // ---------------------------------------------------------------------

  public static buildOptions(
    fieldConf: FormFieldConfig,
    dataLoadingService: SupportClearingDataLoadingService
  ): () => FieldRecord<any>[] {

    switch (fieldConf.field) {
      case 'state':
        return () => dataLoadingService.clearingStates;
      case 'openClose':
        return () => dataLoadingService.openCloses;
      default:
        return () => undefined;
    }
  }

}
